import { capacityLimits, combineEntries, maximum, resolveCapacity } from "./entries.js";
import { evaluateRate } from "./rates.js";
export function runFlows(transaction, flows, numbers, stepSeconds) {
    const zero = numbers.fromNumber(0);
    const start = new Map();
    const inputReservations = new Map();
    const netChanges = new Map();
    const readStart = {
        get: (resource) => {
            let value = start.get(resource);
            if (value === undefined) {
                value = transaction.get(resource);
                start.set(resource, value);
            }
            return value;
        },
        getAllocation: (allocation, targetId) => transaction.getAllocation(allocation.id, targetId),
        purchaseCount: (id) => transaction.getPurchase(id),
    };
    for (const flow of [...flows].sort(compareFlows)) {
        if (!transaction.isScopeActive(flow.scope))
            continue;
        const perSecond = evaluateRate(flow.rate, readStart, numbers);
        let executions = numbers.mul(perSecond, numbers.fromNumber(stepSeconds));
        if (numbers.cmp(executions, zero) < 0 || !numbers.isFinite(executions)) {
            throw new TypeError(`Flow ${flow.id} produced an invalid rate`);
        }
        executions = constrainByInputs(flow, executions, readStart, inputReservations, numbers);
        executions = constrainByCapacity(flow, executions, readStart, netChanges, numbers);
        if (numbers.cmp(executions, zero) === 0)
            continue;
        reserve(flow.consumes, executions, inputReservations, netChanges, numbers, -1);
        reserve(flow.produces, executions, new Map(), netChanges, numbers, 1);
        for (const [resource, coefficient] of combineEntries(flow.produces, numbers)) {
            transaction.addProduction(resource.id, numbers.mul(coefficient, executions));
        }
    }
    for (const [resource, change] of netChanges) {
        let next = numbers.add(readStart.get(resource), change);
        const capacity = resolveCapacity(resource, (entry) => readStart.get(entry), numbers);
        if (capacity !== undefined && numbers.cmp(next, capacity) > 0) {
            next = capacity;
        }
        transaction.set(resource, next);
    }
}
function constrainByInputs(flow, desired, start, reserved, numbers) {
    let feasible = desired;
    for (const [resource, coefficient] of combineEntries(flow.consumes, numbers)) {
        const available = numbers.sub(start.get(resource), reserved.get(resource) ?? numbers.fromNumber(0));
        const bound = numbers.div(available, coefficient);
        if (numbers.cmp(bound, feasible) < 0)
            feasible = maximum(bound, numbers.fromNumber(0), numbers);
    }
    if (flow.onInputShortage === "block" && numbers.cmp(feasible, desired) < 0)
        return numbers.fromNumber(0);
    return feasible;
}
function constrainByCapacity(flow, desired, start, changes, numbers) {
    let feasible = desired;
    const current = (resource) => numbers.add(start.get(resource), changes.get(resource) ?? numbers.fromNumber(0));
    for (const limit of capacityLimits(flow.consumes, flow.produces, current, numbers)) {
        const bound = numbers.div(limit.room, limit.netPerExecution);
        if (numbers.cmp(bound, feasible) < 0)
            feasible = bound;
    }
    return feasible;
}
function reserve(entries, executions, inputs, changes, numbers, direction) {
    for (const [resource, coefficient] of combineEntries(entries, numbers)) {
        const amount = numbers.mul(coefficient, executions);
        if (direction < 0)
            inputs.set(resource, numbers.add(inputs.get(resource) ?? numbers.fromNumber(0), amount));
        const signed = direction < 0 ? numbers.sub(numbers.fromNumber(0), amount) : amount;
        changes.set(resource, numbers.add(changes.get(resource) ?? numbers.fromNumber(0), signed));
    }
}
function compareFlows(left, right) {
    if (left.priority !== right.priority)
        return left.priority - right.priority;
    return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}
//# sourceMappingURL=flows.js.map